import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Globe } from "lucide-react";
import { useApp } from "../context/AppContext";
import { TX } from "../data";
import CartSheet from "./CartSheet";

const AnnouncementBar = () => {
  const { t } = useApp();
  return (
    <div className="w-full bg-[#3A2C28] text-[#F4E6DE] text-[11px] tracking-label uppercase">
      <div className="max-w-[1400px] mx-auto px-5 h-9 flex items-center justify-center gap-3">
        <span className="h-1.5 w-1.5 rounded-full bg-[#E3A99A] animate-pulse" />
        <span>{t.announce}</span>
      </div>
    </div>
  );
};

const Header = () => {
  const { t, lang, toggleLang, cartCount, setCartOpen } = useApp();
  const tx = TX[lang];
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = [
    { label: t.navShop, to: "/#collection" },
    { label: tx.navShade, to: "/shade-finder" },
    { label: t.navRitual, to: "/#manifesto" },
    { label: tx.navFaq, to: "/faq" },
    { label: t.navAbout, to: "/about" },
  ];

  const goTo = (to) => {
    setMobileOpen(false);
    if (to.startsWith("/#")) {
      const id = to.replace("/#", "");
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <>
      <AnnouncementBar />
      <header
        className={`sticky top-0 z-40 w-full transition-all duration-500 ${
          scrolled
            ? "bg-[#FBF5F0]/90 backdrop-blur-md border-b border-[#E7D3C8] shadow-[0_4px_30px_rgba(160,110,90,0.06)]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 h-[72px] flex items-center justify-between">
          <div className="hidden md:flex items-center gap-8 flex-1">
            {nav.map((n) => (
              <button
                key={n.label}
                onClick={() => goTo(n.to)}
                className="link-underline text-[12px] tracking-label uppercase text-[#5A4740] hover:text-[#B4715C]"
              >
                {n.label}
              </button>
            ))}
          </div>

          <button className="md:hidden" onClick={() => setMobileOpen(true)} aria-label="Menu">
            <Menu className="h-5 w-5 text-[#3A2C28]" />
          </button>

          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 font-serif text-[26px] md:text-[30px] tracking-brand text-[#3A2C28]"
          >
            LÉLUNE
          </Link>

          <div className="flex items-center gap-4 md:gap-5 flex-1 justify-end">
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 text-[12px] tracking-label uppercase text-[#5A4740] hover:text-[#B4715C]"
            >
              <Globe className="h-4 w-4" />
              {lang.toUpperCase()}
            </button>
            <button
              onClick={() => setCartOpen(true)}
              className="relative"
              aria-label="Cart"
            >
              <ShoppingBag className="h-5 w-5 text-[#3A2C28] hover:text-[#B4715C] transition-colors" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 h-4.5 w-4.5 min-w-[18px] px-1 rounded-full bg-[#B4715C] text-white text-[10px] flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-[#FBF5F0] md:hidden">
          <div className="px-5 h-[72px] flex items-center justify-between border-b border-[#E7D3C8]">
            <span className="font-serif text-[26px] tracking-brand">LÉLUNE</span>
            <button onClick={() => setMobileOpen(false)} aria-label="Close">
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex flex-col p-6 gap-6">
            {nav.map((n) => (
              <button
                key={n.label}
                onClick={() => goTo(n.to)}
                className="text-left font-serif text-3xl text-[#3A2C28]"
              >
                {n.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      <CartSheet />
    </>
  );
};

export default Header;
